import {
  FocusEvent,
  type HTMLInputTypeAttribute,
  ChangeEvent,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type Ref
} from 'react';
import css from './input.module.scss';
import classNames from 'classnames';
import LocationsInput from '../search-select-input/locations-input';
import { useFormContext } from 'react-hook-form';
import { ReactComponent as WarningIcon } from 'src/assets/form-warning-icon.svg';
import { ONLINE } from 'src/constants/locations';

type ExtractLiteral<T> = T extends string ? (string extends T ? never : T) : never;
type InputType = ExtractLiteral<HTMLInputTypeAttribute> | 'phone' | 'email';

export interface InputProps {
  id: string;
  name: string;
  label: string;
  value?: string;
  type?: InputType;
  defaultValue?: string;
  error?: string;
  onChange?: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onFocus?: (e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onBlur?: (e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  multiline?: boolean;
  searchSelect?: boolean;
  placeholder?: string;
  className?: string;
  onSelect?: () => void;
  entities?: unknown[];
  triggerValidation?: () => void;
  clearErrors?: () => void;
  tabIndex?: number;
  required?: boolean;
  defaultLocation?: { id: string; name: string };
}

const Input = forwardRef<HTMLInputElement | HTMLTextAreaElement, InputProps>((props, ref) => {
  const {
    id,
    name,
    label,
    value: valueFromProps,
    onChange: onChangeFromProps,
    onFocus: onFocusFromProps,
    onBlur: onBlurFromProps,
    error,
    multiline = false,
    searchSelect = false,
    className,
    placeholder,
    ...otherProps
  } = props;

  const isControlled = typeof valueFromProps != 'undefined';
  const hasError = !!error;
  const [isFocused, setIsFocused] = useState(false);
  const { watch } = useFormContext();
  const watchedValue = watch(name);
  const [inputValue, setInputValue] = useState('');

  const onFocus = (e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setIsFocused(true);
    onFocusFromProps?.(e);
  };

  const onBlur = (e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setIsFocused(false);
    onBlurFromProps?.(e);
  };

  const onChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setInputValue(e.target.value);
    onChangeFromProps?.(e);
  };

  const innerRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
  useImperativeHandle(ref, () => innerRef.current!, []);

  const inputProps = {
    name: name,
    id: id,
    ref: innerRef,
    onBlur: onBlur,
    onFocus: onFocus,
    onChange: onChange,
    placeholder: placeholder,
    value: undefined as string | undefined
  };

  if (isControlled) {
    inputProps.value = valueFromProps;
  }

  function renderInput() {
    if (searchSelect) {
      return (
        <LocationsInput
          id={id}
          name={name}
          {...otherProps}
          defaultLocation={otherProps.defaultLocation ?? ONLINE}
          inputProps={{ ...inputProps, className: css.input }}
          showIcons={false}
        />
      );
    }

    if (multiline) {
      return (
        <textarea
          {...otherProps}
          {...inputProps}
          className={css.textarea}
          rows={1}
          ref={ref as Ref<HTMLTextAreaElement>}
        />
      );
    }

    return <input {...otherProps} {...inputProps} className={css.input} ref={ref as Ref<HTMLInputElement>} />;
  }

  useEffect(() => {
    if (multiline && innerRef.current) {
      innerRef.current.style.height = 'auto';
      const scrollHeight = innerRef.current.scrollHeight;
      innerRef.current.style.height = scrollHeight + 'px';
    }
  }, [multiline, inputValue]);

  return (
    <div
      className={classNames(
        css.container,
        !watchedValue ? css.empty : css.filled,
        isFocused ? css.focused : '',
        hasError ? css.error : '',
        otherProps.required ? css.required : '',
        className
      )}>
      <div className={css.labelsContainer}>
        <label className={css.label} htmlFor={id}>
          {label}
        </label>
        {renderInput()}
        {hasError && (
          <label className={css.errorMessage} htmlFor={id}>
            <WarningIcon />
            <span>{error}</span>
          </label>
        )}
      </div>
    </div>
  );
});

export default Input;
